import * as React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import debounce from 'lodash.debounce'
import styled, { css } from 'styled-components'

import Label from './Label'
import gigplanetTheme from '../../../styles/gigplanetTheme'
import {
  searchLocationsAction,
  fetchFylkerIfNeededAction,
} from '../../../stores/Locations/LocationsActions'
import { getFylkeNamesSelector } from '../../../stores/Locations/LocationsReducer'
import { searchFylker, getCanonicalFylke } from '../../../models/Fylke'
import {
  LocationFilterShape,
  createKommuneLocationFilter,
  createFylkeLocationFilter,
  getLocationFilterLabel,
} from '../../../models/LocationFilter'

const Container = styled.div`
  position: relative;
  width: 100%;
  text-align: left;
`

const InputWrapper = styled.div`
  position: relative;
`

const InputElement = styled.input`
  width: 100%;
  appearance: none;
  background-color: ${gigplanetTheme.component.form.input.background};
  border: 1px solid ${gigplanetTheme.component.form.input.border};
  border-radius: 2px;
  color: ${gigplanetTheme.component.form.input.text};
  font-size: 16px;
  font-weight: 300;
  line-height: 20px;
  padding: 8px 32px 8px 10px;
  outline: none;
  transition: border-color 0.2s ease-in-out;

  &:focus {
    border-color: ${gigplanetTheme.color.purple};
  }
`

const ResetButton = styled.button`
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  background: transparent;
  border: 0;
  color: ${gigplanetTheme.component.form.input.text};
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 4px 8px;
`

const DropdownList = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid ${gigplanetTheme.component.form.input.border};
  border-radius: 4px;
  max-height: 320px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
`

const GroupHeading = styled.li`
  padding: 6px 12px 2px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${gigplanetTheme.color.greyMedium || '#888'};
`

const DropdownItem = styled.li`
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: 15px;

  ${({ isActive }) =>
    isActive &&
    css`
      background-color: ${gigplanetTheme.color.purpleLight || '#f4eef8'};
    `};

  &:hover {
    background-color: ${gigplanetTheme.color.purpleLight || '#f4eef8'};
  }
`

const PlaceName = styled.span`
  font-weight: 500;
`

const PlaceMeta = styled.span`
  font-size: 12px;
  color: ${gigplanetTheme.color.greyMedium || '#888'};
`

const EmptyState = styled.div`
  padding: 10px 12px;
  font-size: 13px;
  color: ${gigplanetTheme.color.greyMedium || '#888'};
  text-align: center;
`

const MAX_FYLKE_SUGGESTIONS = 5

/*
  The fylker the matching kommuner belong to - so searching "Arendal" also offers to search all
  of Agder. It comes free with the searchKommune payload, no extra request.

  These can only be known once the kommune request resolves, so they are rendered BELOW the
  kommuner. Putting them in the top group would insert a row above the one the user is already
  reaching for, at an unpredictable moment - an easy way to mis-click.
*/
const buildRelatedFylker = (fylkeNames, kommuner, alreadyListed) => {
  const names = []
  kommuner.forEach(kommune => {
    const canonical = getCanonicalFylke(fylkeNames, kommune.fylke)
    if (canonical && alreadyListed.indexOf(canonical) === -1 && names.indexOf(canonical) === -1) {
      names.push(canonical)
    }
  })
  return names.slice(0, MAX_FYLKE_SUGGESTIONS)
}

class LocationAutocomplete extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      query: getLocationFilterLabel(props.value),
      kommuner: [],
      fylker: [],
      relatedFylker: [],
      activeIdx: -1,
      isOpen: false,
    }
    this.containerRef = React.createRef()
    this.debouncedSearch = debounce(this.runSearch, 250, { leading: false })
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleOutsideClick)
    // Cached for 4h, so this is a no-op after the first mount anywhere in the app.
    this.props.fetchFylkerIfNeeded()
  }

  componentDidUpdate(prevProps) {
    const label = getLocationFilterLabel(this.props.value)
    if (getLocationFilterLabel(prevProps.value) !== label && !this.state.isOpen) {
      this.setState({ query: label })
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick)
  }

  // Flat list of selectable options in render order, so keyboard nav walks every group.
  getOptions() {
    const { fylker, kommuner, relatedFylker } = this.state
    return fylker
      .map(name => ({ type: 'fylke', key: `fylke-${name}`, name }))
      .concat(kommuner.map(kommune => ({ type: 'kommune', key: `kommune-${kommune.id}`, kommune })))
      .concat(relatedFylker.map(name => ({ type: 'fylke', key: `related-${name}`, name })))
  }

  handleOutsideClick = e => {
    if (this.containerRef.current && !this.containerRef.current.contains(e.target)) {
      this.setState({ isOpen: false, activeIdx: -1 })
    }
  }

  runSearch = q => {
    const { searchLocations, includeFylker } = this.props
    if (!q || q.length < 1) {
      this.setState({ kommuner: [], fylker: [], relatedFylker: [], isOpen: false, activeIdx: -1 })
      return
    }
    const { fylkeNames } = this.props
    searchLocations(q, 20).then(results => {
      if (this.state.query !== q) return
      const kommuner = results || []
      const fylker = includeFylker ? searchFylker(fylkeNames, q, MAX_FYLKE_SUGGESTIONS) : []
      this.setState({
        kommuner,
        fylker,
        relatedFylker: includeFylker ? buildRelatedFylker(fylkeNames, kommuner, fylker) : [],
        isOpen: true,
        activeIdx: -1,
      })
    })
  }

  handleChange = e => {
    const query = e.target.value
    const { includeFylker, fylkeNames } = this.props
    // Name matches are local, so the top group renders instantly and never reorders once the
    // kommune request lands - only the groups below it grow.
    this.setState({
      query,
      isOpen: true,
      fylker: includeFylker ? searchFylker(fylkeNames, query, MAX_FYLKE_SUGGESTIONS) : [],
      relatedFylker: [],
    })
    this.debouncedSearch(query)
    if (!query && this.props.onReset) {
      this.props.onReset()
    }
  }

  // Selecting either kind replaces the whole selection - there is only ever one location filter.
  handleSelectOption = option => {
    const locationFilter =
      option.type === 'fylke'
        ? createFylkeLocationFilter(option.name)
        : createKommuneLocationFilter(option.kommune)
    if (!locationFilter) return
    this.setState({
      query: getLocationFilterLabel(locationFilter),
      isOpen: false,
      activeIdx: -1,
    })
    this.props.onChange(locationFilter)
  }

  handleKeyDown = e => {
    const { activeIdx, isOpen } = this.state
    const options = this.getOptions()
    if (!isOpen || options.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      this.setState({ activeIdx: Math.min(activeIdx + 1, options.length - 1) })
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      this.setState({ activeIdx: Math.max(activeIdx - 1, 0) })
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const pick = activeIdx >= 0 ? options[activeIdx] : options[0]
      if (pick) this.handleSelectOption(pick)
    } else if (e.key === 'Escape') {
      this.setState({ isOpen: false, activeIdx: -1 })
    }
  }

  handleFocus = () => {
    if (this.state.query && this.getOptions().length > 0) {
      this.setState({ isOpen: true })
    }
  }

  handleReset = () => {
    this.setState({
      query: '',
      kommuner: [],
      fylker: [],
      relatedFylker: [],
      isOpen: false,
      activeIdx: -1,
    })
    if (this.props.onReset) this.props.onReset()
  }

  renderOption(option, index) {
    const { activeIdx } = this.state
    const isFylke = option.type === 'fylke'
    let meta = null
    if (isFylke) {
      meta = <PlaceMeta>Hele fylket</PlaceMeta>
    } else if (option.kommune.fylke) {
      meta = <PlaceMeta>{option.kommune.fylke}</PlaceMeta>
    }
    return (
      <DropdownItem
        key={option.key}
        role="option"
        aria-selected={index === activeIdx}
        isActive={index === activeIdx}
        onMouseDown={e => {
          e.preventDefault()
          this.handleSelectOption(option)
        }}
      >
        <PlaceName>{isFylke ? option.name : option.kommune.name}</PlaceName>
        {meta}
      </DropdownItem>
    )
  }

  render() {
    const { id, label, placeholder } = this.props
    const { query, fylker, kommuner, isOpen } = this.state
    const options = this.getOptions()
    const kommuneGroupStart = fylker.length
    const relatedGroupStart = fylker.length + kommuner.length

    return (
      <Container ref={this.containerRef}>
        {label ? <Label htmlFor={id} label={label} showValueMarker={!!query} /> : null}
        <InputWrapper>
          <InputElement
            id={id}
            type="text"
            value={query}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            onFocus={this.handleFocus}
            placeholder={placeholder}
            aria-label={label || placeholder}
            autoComplete="off"
            spellCheck="false"
          />
          {query ? (
            <ResetButton type="button" onClick={this.handleReset} aria-label="Tøm">
              ×
            </ResetButton>
          ) : null}
          {isOpen ? (
            <DropdownList role="listbox">
              {options.length === 0 ? (
                <EmptyState>Ingen treff</EmptyState>
              ) : (
                options.map((option, index) => (
                  <React.Fragment key={option.key}>
                    {index === 0 && fylker.length > 0 ? (
                      <GroupHeading aria-hidden="true">Fylker</GroupHeading>
                    ) : null}
                    {index === kommuneGroupStart && kommuner.length > 0 ? (
                      <GroupHeading aria-hidden="true">Kommuner</GroupHeading>
                    ) : null}
                    {index === relatedGroupStart && index < options.length ? (
                      <GroupHeading aria-hidden="true">Utvid søket</GroupHeading>
                    ) : null}
                    {this.renderOption(option, index)}
                  </React.Fragment>
                ))
              )}
            </DropdownList>
          ) : null}
        </InputWrapper>
      </Container>
    )
  }
}

LocationAutocomplete.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: LocationFilterShape,
  includeFylker: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  searchLocations: PropTypes.func.isRequired,
  fetchFylkerIfNeeded: PropTypes.func.isRequired,
  fylkeNames: PropTypes.arrayOf(PropTypes.string),
}

LocationAutocomplete.defaultProps = {
  label: '',
  placeholder: 'Søk etter fylke eller kommune',
  value: null,
  includeFylker: true,
  onReset: null,
  fylkeNames: [],
}

const mapStateToProps = state => ({
  fylkeNames: getFylkeNamesSelector(state),
})

const mapDispatchToProps = dispatch => ({
  searchLocations: (q, limit) => dispatch(searchLocationsAction(q, limit)),
  fetchFylkerIfNeeded: () => dispatch(fetchFylkerIfNeededAction()),
})

export default connect(mapStateToProps, mapDispatchToProps)(LocationAutocomplete)
